<template>
  <div class="login-container">
    <div class="left-form">
      <img src="@/assets/images/common/drip-logo.svg" />
      <h1 class="heading">{{ $t('newResetPassword.heading') }}</h1>
      <p class="mb-0">{{ $t('newResetPassword.subHeading') }}</p>

      <p class="mt-4 mb-0">{{ $t('newResetPassword.email') }}</p>
      <p class="mb-2">{{ email }}</p>

      <b-form @submit.prevent="resetPassword" novalidate>
        <div class="status-card-container">
          <error-card
            :error="error"
            link="/signup"
            linkText="Signup"
            :text="errorText"
          />
        </div>
        <div class="form-container">
          <div class="form-field">
            <h3 class="label">
              {{ $t('newResetPassword.resetForm.passwordLabel') }}
            </h3>
            <div class="position-relative">
              <input
                type="password"
                class="input-field"
                :class="{ 'input-error': errors.first('password') }"
                v-model="formData.password"
                :bails="false"
                v-validate.continues="
                  'required|min:8|max:14|upper-case|spl-char|one-digit'
                "
                data-vv-validate-on="input"
                data-vv-as="Password"
                name="password"
                ref="password"
                @input="validationTests"
              />
              <div class="mt-2 error-block" v-if="errors.first('password')">
                <span class="error">
                  <b-icon-x-circle
                    v-if="errors.first('password')"
                    variant="danger"
                  />
                  {{ errors.first('password') }}
                </span>
              </div>
              <b-icon-exclamation-circle-fill
                variant="secondary"
                class="input-icon"
                id="popover-target-1"
              />
              <b-popover
                target="popover-target-1"
                placement="right"
                :show.sync="showValidations"
                class=""
              >
                <b-form-checkbox
                  class="checks"
                  :checked="validations.minMax"
                  disabled
                  id="checkbox-1"
                  name="checkbox-1"
                >
                  {{ $t('newResetPassword.validations.minMax') }}
                </b-form-checkbox>
                <b-form-checkbox
                  class="checks"
                  disabled
                  :checked="validations.upperCase"
                  id="checkbox-2"
                  name="checkbox-2"
                >
                  {{ $t('newResetPassword.validations.upperCase') }}
                </b-form-checkbox>
                <b-form-checkbox
                  class="checks"
                  disabled
                  :checked="validations.splChar"
                  id="checkbox-3"
                  name="checkbox-3"
                >
                  {{ $t('newResetPassword.validations.splChar') }}
                </b-form-checkbox>
                <b-form-checkbox
                  class="checks"
                  disabled
                  :checked="validations.oneDigit"
                  id="checkbox-4"
                  name="checkbox-4"
                >
                  {{ $t('newResetPassword.validations.oneDigit') }}
                </b-form-checkbox>
              </b-popover>
            </div>
          </div>
          <div class="form-field">
            <h3 class="label">
              {{ $t('newResetPassword.resetForm.confirmLabel') }}
            </h3>
            <input
              type="password"
              class="input-field"
              :class="{ 'input-error': errors.first('password_confirmation') }"
              v-model="formData.password_confirmation"
              v-validate="'required|confirmed:password'"
              data-vv-validate-on="input"
              data-vv-as="Confirm Password"
              name="password_confirmation"
            />
            <div
              class="mt-2 error-block"
              v-if="errors.first('password_confirmation')"
            >
              <span class="error">
                <b-icon-x-circle
                  v-if="errors.first('password_confirmation')"
                  variant="danger"
                />
                {{ errors.first('password_confirmation') }}
              </span>
            </div>
          </div>
          <action-button
            type="submit"
            variant="solid"
            class="pt-3 text-center mt-0 w-100"
          >
            <b-icon-arrow-repeat
              v-if="loading"
              class="mr-2 color-white"
              animation="spin"
            />
            {{ $t('newResetPassword.resetForm.resetButton') }}
          </action-button>
        </div>
      </b-form>
    </div>
    <image-container />
  </div>
</template>
<script>
import ErrorCard from '@/components/validation/ErrorCard'
import ActionButton from '@/components/common/ActionButton'
import userService from '@/services/user'
import ImageContainer from './ImageContainer.vue'

export default {
  data() {
    return {
      success: false,
      error: false,
      loading: false,
      publicToken: '',
      email: '-',
      formData: {
        password: '',
        password_confirmation: ''
      },
      errorText: this.$t('newResetPassword.error.text', {
        email: this.$route.query.email
      }),
      showValidations: true,
      validations: {
        minMax: false,
        upperCase: false,
        splChar: false,
        oneDigit: false
      }
    }
  },
  components: {
    ErrorCard,
    ActionButton,
    ImageContainer
  },
  created() {
    this.$validator.extend('upper-case', {
      getMessage(field, val) {
        return 'One upper case character'
      },
      validate(value, field) {
        return /(?=.*[A-Z])/.test(value)
      }
    })
    this.$validator.extend('one-digit', {
      getMessage(field, val) {
        return 'Requires one number'
      },
      validate(value, field) {
        return /(?=.*\d)/.test(value)
      }
    })
    this.$validator.extend('spl-char', {
      getMessage(field, val) {
        return 'Requires one special character'
      },
      validate(value, field) {
        return /(?=.*\W)/.test(value)
      }
    })
  },
  mounted() {
    document.title = 'Forgot Password | Drip Portal'
    this.$mpTrack('Reset password - Page opened', {
      email: this.$route.query.email
    })
    this.getPublicToken()
  },
  methods: {
    getEmail(publicToken, code) {
      return userService
        .fetchEmailFromToken(publicToken, code)
        .then((res) => {
          console.log('email', res.data)
          this.email = res.data.email
        })
        .catch((err) => {
          console.log(err)
        })
    },
    getPublicToken() {
      return userService
        .fetchAccessToken()
        .then((response) => {
          this.publicToken = response.data.token
          this.getEmail(this.publicToken, this.$route.params.code)
        })
        .catch((err) => {
          console.log(err)
        })
    },
    resetPassword() {
      return this.$validator
        .validate()
        .then((result) => {
          if (result) {
            this.$mpTrack('Reset password - Reset button clicked', {
              email: this.$route.query.email
            })
            this.sendRequest()
          }
        })
        .catch((err) => {
          console.log(err)
        })
    },
    sendRequest() {
      if (!this.loading) {
        this.loading = true
        return userService
          .resetPassword(this.$route.params.code, {
            user: this.formData,
            api_token: this.publicToken
          })
          .then((res) => {
            if (res.status === 200) {
              this.$mpTrack('Reset password - Password reset success', {
                email: this.$route.query.email
              })
              this.$router.push('/login?resetpassword=success')
            } else {
              this.$mpTrack('Reset password - Password reset failed', {
                email: this.$route.query.email
              })
              this.loading = false
            }
          })
          .catch((e) => {
            this.loading = false
            this.raiseError(e)
          })
      }
    },
    raiseError(err) {
      this.$mpTrack('Reset password - API Failed', {
        email: this.$route.query.email,
        error: err?.response?.data?.message || err
      })
      return this.$notify({
        title: this.$t('notifications.passwordError.title'),
        text: this.$t('notifications.passwordError.text'),
        type: 'error'
      })
    },
    validationTests(e) {
      this.errors.items.forEach((error) => {
        switch (error.rule) {
          case 'min':
            this.validations.minMax = false
            break
          case 'upper-case':
            this.validations.upperCase = false
            break
          case 'spl-char':
            this.validations.splChar = false
            break
          case 'one-digit':
            this.validations.oneDigit = false
            break
        }
      })
      if (this.errors.items.length === 0) {
        this.validations.minMax = false
        this.validations.upperCase = false
        this.validations.splChar = false
        this.validations.oneDigit = true
      }
      if (e.target.value === '') {
        this.validations.minMax = true
        this.validations.upperCase = true
        this.validations.splChar = true
        this.validations.oneDigit = false
      }
    },
    reValidateTests(value) {
      if (/^.*[A-Z].*$/.test(value)) {
        this.validations.upperCase = true
      } else {
        this.validations.upperCase = false
      }
      if (/(?=.*\d)/.test(value)) {
        this.validations.oneDigit = true
      } else {
        this.validations.oneDigit = false
      }
      if (/(?=.*\W)/.test(value)) {
        this.validations.splChar = true
      } else {
        this.validations.splChar = false
      }
      if (/^[A-Za-z0-9\W]{8,14}$/.test(value)) {
        this.validations.minMax = true
      } else {
        this.validations.minMax = false
      }
    }
  },
  watch: {
    'formData.password'(newPassword) {
      this.reValidateTests(newPassword)
    }
  }
}
</script>
<style scoped>
.input-icon {
  width: 25px;
  height: 30px;
  right: 10px;
  position: absolute;
  top: 15px;
}
.checks {
  padding-bottom: 10px;
}
</style>
